import { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  useGetSocietyByCodeQuery,
  useGetSocietySettingsQuery,
} from '@/publicSearch/graphql/queries.generated';
import { Society } from '@/publicSearch/store/usePublicSearchContext';
import { useSocietyAccessByCode } from './useSocietyAccessByCode';

export type CurrentSociety = Society & {
  notFound: boolean;
  requiresLink: boolean;
};

export function useCurrentSociety(): CurrentSociety {
  const pathParams = useParams<{ orgName?: string }>();
  const societyCode = pathParams.orgName?.trim() || '';

  const {
    data: societyData,
    loading: societyLoading,
    error: societyError,
  } = useGetSocietyByCodeQuery({
    skip: !societyCode,
    variables: { societyCode },
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    nextFetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });

  const society = societyData?.societies?.items?.[0];

  const {
    access,
    loading: accessLoading,
    requiresLink,
  } = useSocietyAccessByCode(societyCode);

  const resolvedSocietyId = society?.societyId ?? access?.societyId;
  const orgId = String(resolvedSocietyId || '');
  const resolvedName = society?.name ?? access?.name ?? '';
  const resolvedSocietyCode = society?.societyCode ?? access?.societyCode ?? '';

  const { data: settingsData, loading: settingsLoading } =
    useGetSocietySettingsQuery({
      skip: !orgId || requiresLink,
      context: { headers: setDefaultHeader(orgId) },
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      nextFetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    });

  const loading =
    societyLoading || accessLoading || (!requiresLink && settingsLoading);
  const settings = settingsData?.societySettings;

  const notFound =
    !!societyCode &&
    !societyLoading &&
    !accessLoading &&
    !societyError &&
    !requiresLink &&
    !society &&
    !!access &&
    (!access.exists || !access.canAccess);

  return useMemo(
    () =>
      ({
        ...society,
        societyId: resolvedSocietyId,
        societyCode: resolvedSocietyCode,
        settings,
        loading,
        name: resolvedName,
        orgId,
        notFound,
        requiresLink,
      }) as CurrentSociety,
    [
      loading,
      orgId,
      society,
      settings,
      notFound,
      requiresLink,
      resolvedName,
      resolvedSocietyCode,
      resolvedSocietyId,
    ],
  );
}
